import { apiClient } from './client.js'

// ==================== SALES ORDERS (Penjualan) ====================

/**
 * Get list of sales orders with pagination and filters
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number (default: 1)
 * @param {number} params.limit - Records per page (default: 20, max: 100)
 * @param {string} params.status - Filter by order status (PENDING, DONE, CANCELLED)
 * @param {string} params.customer_id - Filter by customer ID (UUID)
 * @param {string} params.seller_type - Filter by seller type (INTERNAL, EXTERNAL, ONLINE)
 * @param {string} params.payment_status - Filter by payment status (UNPAID, PARTIAL, PAID, REFUNDED)
 * @param {string} params.start_date - Start date filter (YYYY-MM-DD)
 * @param {string} params.end_date - End date filter (YYYY-MM-DD)
 * @param {string} params.query - Search in customer name or transaction number
 */
export const getSalesOrders = async (params = {}) => {
  const query = new URLSearchParams()
  const toCsv = (value) => (Array.isArray(value) ? value.filter(Boolean).join(',') : value)

  if (params.page) query.append('page', params.page)
  if (params.limit) query.append('limit', params.limit)
  if (params.status) query.append('status', toCsv(params.status))
  if (params.customer_id) query.append('customer_id', params.customer_id)
  if (params.seller_type) query.append('seller_type', params.seller_type)
  if (params.payment_status) query.append('payment_status', toCsv(params.payment_status))
  if (params.start_date) query.append('start_date', params.start_date)
  if (params.end_date) query.append('end_date', params.end_date)
  if (params.query) query.append('query', params.query)
  
  return apiClient.get(`/sales/orders?${query.toString()}`)
}

/**
 * Create new sales order
 * @param {Object} data - Order data
 * @param {string} data.customer_id - Customer ID (optional, for registered customers)
 * @param {string} data.customer_name - Customer name (optional, for walk-in customers)
 * @param {string} data.seller_type - Seller type (INTERNAL, EXTERNAL, ONLINE)
 * @param {Array} data.items - Order items array
 * @param {number} data.additional_costs - Delivery, service fees, etc.
 * @param {number} data.discount_total - Total discount amount
 * @param {Array} data.payments - Payment methods array
 * @param {string} data.notes - Additional order notes
 */
export const createSalesOrder = async (data) => {
  return apiClient.post('/sales/orders', data)
}

/**
 * Get sales order details by ID
 * @param {string} id - Order ID (UUID)
 */
export const getSalesOrderDetail = async (id) => {
  return apiClient.get(`/sales/orders/${id}`)
}

/**
 * Update sales order status
 * @param {string} id - Order ID (UUID)
 * @param {Object} data - Status update data
 * @param {string} data.status - New status (PENDING, DONE, CANCELLED)
 */
export const updateSalesOrderStatus = async (id, data) => {
  return apiClient.put(`/sales/orders/${id}/status`, data)
}

/**
 * Update sales order details
 * @param {string} id - Order ID (UUID)  
 * @param {Object} data - Updated order data
 */
export const updateSalesOrder = async (id, data) => {
  return apiClient.put(`/sales/orders/${id}`, data)
}

/**
 * Delete sales order (soft delete)
 * @param {string} id - Order ID (UUID)
 */
export const deleteSalesOrder = async (id, data) => {
  return apiClient.delete(`/sales/orders/${id}`, data)
}

// ==================== CANCELLATION ====================

/**
 * Request cancellation for a PENDING order
 * @param {string} id - Order ID (UUID)
 * @param {Object} data
 * @param {string} data.reason - Reason for cancellation
 */
export const requestOrderCancellation = async (id, data) => {
  return apiClient.post(`/sales/orders/${id}/cancellation/request`, data)
}

/**
 * Approve a CANCEL_REQUESTED order (no body required)
 * @param {string} id - Order ID (UUID)
 */
export const approveOrderCancellation = async (id) => {
  return apiClient.post(`/sales/orders/${id}/cancellation/approve`)
}

export const voidOrderItem = async (orderId, itemId) => {
  return apiClient.delete(`/sales/orders/${orderId}/items/${itemId}/void`)
}

// ==================== ORDER PAYMENTS ====================

/**
 * Add payment to existing order
 * @param {string} orderId - Order ID (UUID)
 * @param {Object} paymentData - Payment information
 * @param {string} paymentData.payment_method - Payment method (CASH, QRIS, TRANSFER, MIXED)
 * @param {number} paymentData.cash_amount - Cash portion of payment
 * @param {number} paymentData.electronic_amount - Electronic portion of payment
 * @param {string} paymentData.account_destination - Bank account or e-wallet details
 * @param {string} paymentData.payment_proof_url - URL to payment proof image
 * @param {string} paymentData.notes - Payment-specific notes
 */
export const addOrderPayment = async (orderId, paymentData) => {
  return apiClient.post(`/sales/orders/${orderId}/payments`, paymentData)
}

/**
 * Get order payment history
 * @param {string} orderId - Order ID (UUID)
 */
export const getOrderPayments = async (orderId) => {
  return apiClient.get(`/sales/orders/${orderId}/payments`)
}

/**
 * Update payment status
 * @param {string} orderId - Order ID (UUID)
 * @param {string} paymentId - Payment ID (UUID)
 * @param {Object} data - Payment status update
 * @param {string} data.payment_status - New payment status (PENDING, COMPLETED, FAILED)
 */
export const updatePaymentStatus = async (orderId, paymentId, data) => {
  return apiClient.put(`/sales/orders/${orderId}/payments/${paymentId}/status`, data)
}

// ==================== ORDER ITEMS ====================

/**
 * Get order items by order ID
 * @param {string} orderId - Order ID (UUID)
 */
export const getOrderItems = async (orderId) => {
  return apiClient.get(`/sales/orders/${orderId}/items`)
}

/**
 * Add item to existing order
 * @param {string} orderId - Order ID (UUID)
 * @param {Object} itemData - Item information
 * @param {string} itemData.item_type - Item type (PRODUCT, SERVICE)
 * @param {string} itemData.product_id - Product ID (if item_type = PRODUCT)
 * @param {string} itemData.product_variant_id - Product variant ID (optional)
 * @param {string} itemData.service_id - Service ID (if item_type = SERVICE)
 * @param {number} itemData.quantity - Quantity ordered
 * @param {string} itemData.unit_id - Unit ID (optional)
 * @param {number} itemData.unit_price - Price per unit
 * @param {string} itemData.notes - Item-specific notes
 */
export const addOrderItem = async (orderId, itemData) => {
  return apiClient.post(`/sales/orders/${orderId}/items`, itemData)
}

/**
 * Update order item
 * @param {string} orderId - Order ID (UUID)
 * @param {string} itemId - Item ID (UUID)
 * @param {Object} itemData - Updated item data
 */
export const updateOrderItem = async (orderId, itemId, itemData) => {
  return apiClient.put(`/sales/orders/${orderId}/items/${itemId}`, itemData)
}

/**
 * Remove item from order
 * @param {string} orderId - Order ID (UUID)
 * @param {string} itemId - Item ID (UUID)
 */
export const removeOrderItem = async (orderId, itemId) => {
  return apiClient.delete(`/sales/orders/${orderId}/items/${itemId}`)
}

// ==================== SALES ANALYTICS ====================

/**
 * Get sales summary statistics
 * @param {Object} params - Filter parameters
 * @param {string} params.start_date - Start date (YYYY-MM-DD)
 * @param {string} params.end_date - End date (YYYY-MM-DD)
 * @param {string} params.seller_type - Filter by seller type
 */
export const getSalesSummary = async (params = {}) => {
  const query = new URLSearchParams()
  if (params.start_date) query.append('start_date', params.start_date)
  if (params.end_date) query.append('end_date', params.end_date)
  if (params.seller_type) query.append('seller_type', params.seller_type)
  
  return apiClient.get(`/sales/summary?${query.toString()}`)
}

/**
 * Get sales trends data for charts
 * @param {Object} params - Filter parameters
 * @param {string} params.period - Period type (daily, weekly, monthly)
 * @param {string} params.start_date - Start date (YYYY-MM-DD)
 * @param {string} params.end_date - End date (YYYY-MM-DD)
 */
export const getSalesTrends = async (params = {}) => {
  const query = new URLSearchParams()
  if (params.period) query.append('period', params.period)
  if (params.start_date) query.append('start_date', params.start_date)
  if (params.end_date) query.append('end_date', params.end_date)
  
  return apiClient.get(`/sales/trends?${query.toString()}`)
}

/**
 * Get top selling products
 * @param {Object} params - Filter parameters
 * @param {number} params.limit - Number of products to return (default: 10)
 * @param {string} params.start_date - Start date (YYYY-MM-DD)
 * @param {string} params.end_date - End date (YYYY-MM-DD)
 */
export const getTopSellingProducts = async (params = {}) => {
  const query = new URLSearchParams()
  if (params.limit) query.append('limit', params.limit)
  if (params.start_date) query.append('start_date', params.start_date)
  if (params.end_date) query.append('end_date', params.end_date)
  
  return apiClient.get(`/sales/top-products?${query.toString()}`)
}

// ==================== TRANSACTION HELPERS ====================

/**
 * Generate new transaction number
 */
export const generateTransactionNumber = async () => {
  return apiClient.get('/sales/generate-transaction-number')
}

/**
 * Validate order before creation (check stock, customer limits, etc.)
 * @param {Object} orderData - Order data to validate
 */
export const validateOrder = async (orderData) => {
  return apiClient.post('/sales/validate-order', orderData)
}

/**
 * Calculate order totals (subtotal, discounts, grand total)
 * @param {Object} calculationData - Data for calculation
 * @param {Array} calculationData.items - Order items
 * @param {number} calculationData.additional_costs - Additional costs
 * @param {number} calculationData.discount_total - Discount amount
 */
export const calculateOrderTotals = async (calculationData) => {
  return apiClient.post('/sales/calculate-totals', calculationData)
}

// ==================== QUICK SALES ====================

/**
 * Create quick sale (for simple cash transactions)
 * @param {Object} quickSaleData - Quick sale data
 * @param {Array} quickSaleData.items - Items being sold
 * @param {string} quickSaleData.customer_name - Customer name (optional)
 * @param {number} quickSaleData.cash_received - Cash received from customer
 */
export const createQuickSale = async (quickSaleData) => {
  return apiClient.post('/sales/quick-sale', quickSaleData)
}

// ==================== REFUNDS & RETURNS ====================

/**
 * Process order refund
 * @param {string} orderId - Order ID to refund
 * @param {Object} refundData - Refund details
 * @param {Array} refundData.items - Items to refund (with quantities)
 * @param {string} refundData.reason - Refund reason
 * @param {number} refundData.refund_amount - Amount to refund
 */
export const processRefund = async (orderId, refundData) => {
  return apiClient.post(`/sales/orders/${orderId}/refund`, refundData)
}

/**
 * Get refund history for an order
 * @param {string} orderId - Order ID
 */
export const getRefundHistory = async (orderId) => {
  return apiClient.get(`/sales/orders/${orderId}/refunds`)
}

// ==================== PRINT & EXPORT ====================

/**
 * Get order receipt data for printing
 * @param {string} orderId - Order ID
 */
export const getOrderReceipt = async (orderId) => {
  return apiClient.get(`/sales/orders/${orderId}/receipt`)
}

/**
 * Export sales data to CSV/Excel
 * @param {Object} params - Export parameters
 * @param {string} params.format - Export format (csv, excel)
 * @param {string} params.start_date - Start date filter
 * @param {string} params.end_date - End date filter
 * @param {Array} params.columns - Columns to include
 */
export const exportSalesData = async (params = {}) => {
  const query = new URLSearchParams()
  if (params.format) query.append('format', params.format)
  if (params.start_date) query.append('start_date', params.start_date)
  if (params.end_date) query.append('end_date', params.end_date)
  if (params.columns && params.columns.length > 0) {
    params.columns.forEach(col => query.append('columns', col))
  }
  
  return apiClient.get(`/sales/export?${query.toString()}`)
}

// ==================== BEST SELLER ====================

/**
 * Get best seller products (products ordered more than configured minimum)
 * @param {Object} params - Query parameters
 * @param {string} params.query - Search query
 * @param {number} params.limit - Max products to return
 */
export const getBestSellerProducts = async (params = {}) => {
  const query = new URLSearchParams(params)
  query.append('_t', new Date().getTime())
  return apiClient.get(`/sales/best-seller?${query.toString()}`)
}

/**
 * Get Best Seller configuration
 */
export const getBestSellerConfig = async () => {
  return apiClient.get(`/config/best-seller?_t=${new Date().getTime()}`)
}

/**
 * Update Best Seller configuration
 * @param {Object} config - Configuration data
 * @param {boolean} config.enabled - Enable/disable feature
 * @param {number} config.min_orders - Minimum orders threshold
 * @param {number} config.max_products - Max products to show
 */
export const updateBestSellerConfig = async (config) => {
  return apiClient.put(`/config/best-seller?_t=${new Date().getTime()}`, config)
}
